import type { ReactNode } from 'react';
import {
  AlertTriangle,
  CalendarClock,
  Factory,
  Flag,
  Info,
  PackageCheck,
  PackageOpen,
  ShoppingCart,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { formatQtyUom } from '@/lib/uom';
import { PLANNED_ORDER_STATUS_LABELS, fmtQty } from '@/features/manufacturing/planning/engine';
import type {
  CalendarEventKind,
  CalendarEventSeverity,
  DataGapSeverity,
  PlannedOrderStatus,
  PlanningSupplier,
} from '@/features/manufacturing/planning/types';
import { formatDateOnly } from '@/lib/formatters';
import { cn } from '@/lib/utils';

type BadgeVariant = NonNullable<BadgeProps['variant']>;

export const ORDER_STATUS_VARIANT: Record<PlannedOrderStatus, BadgeVariant> = {
  late: 'warning',
  order_now: 'warning-soft',
  order_soon: 'info',
  unknown_lead_time: 'muted',
  ok: 'success-soft',
};

export function OrderStatusBadge({ status, className }: { status: PlannedOrderStatus; className?: string }) {
  return (
    <Badge variant={ORDER_STATUS_VARIANT[status]} className={cn('whitespace-nowrap', className)}>
      {PLANNED_ORDER_STATUS_LABELS[status]}
    </Badge>
  );
}

export const GAP_SEVERITY_VARIANT: Record<DataGapSeverity, BadgeVariant> = {
  blocking: 'warning',
  warning: 'warning-soft',
  info: 'muted',
};

export const GAP_SEVERITY_LABEL: Record<DataGapSeverity, string> = {
  blocking: 'Blocking',
  warning: 'Check',
  info: 'FYI',
};

export const GAP_SEVERITY_ICON: Record<DataGapSeverity, LucideIcon> = {
  blocking: AlertTriangle,
  warning: Flag,
  info: Info,
};

export interface EventKindMeta {
  label: string;
  icon: LucideIcon;
  /** Chip classes on the calendar grid. */
  chip: string;
  dot: string;
}

export const EVENT_KIND_META: Record<CalendarEventKind, EventKindMeta> = {
  order_ideal: {
    label: 'Order (ideal day)',
    icon: ShoppingCart,
    chip: 'border-info-soft/70 bg-info-soft/30 text-info',
    dot: 'bg-info',
  },
  order_latest: {
    label: 'Last day to order',
    icon: CalendarClock,
    chip: 'border-fresh-salmon/60 bg-fresh-salmon/25 text-fresh-salmon-dark',
    dot: 'bg-fresh-salmon-dark',
  },
  runout: {
    label: 'Stock runs out',
    icon: PackageOpen,
    chip: 'border-transparent bg-fresh-salmon-dark text-linen',
    dot: 'bg-fresh-salmon-dark',
  },
  receipt_expected: {
    label: 'PO arrives',
    icon: PackageCheck,
    chip: 'border-bright-moss/60 bg-bright-moss/30 text-bright-moss-dark',
    dot: 'bg-bright-moss-dark',
  },
  planned_receipt: {
    label: 'Suggested order lands',
    icon: PackageCheck,
    chip: 'border-dashed border-bright-moss/60 bg-transparent text-bright-moss-dark',
    dot: 'bg-bright-moss',
  },
  po_order_by: {
    label: 'Place draft PO',
    icon: ShoppingCart,
    chip: 'border-fresh-salmon/60 bg-fresh-salmon/15 text-fresh-salmon-dark',
    dot: 'bg-fresh-salmon',
  },
  production: {
    label: 'Build target',
    icon: Factory,
    chip: 'border-border bg-secondary text-muted-foreground',
    dot: 'bg-muted-foreground',
  },
  holiday: {
    label: 'Holiday',
    icon: Sun,
    chip: 'border-border bg-muted text-muted-foreground',
    dot: 'bg-border',
  },
};

export const EVENT_SEVERITY_RING: Record<CalendarEventSeverity, string> = {
  critical: 'ring-1 ring-fresh-salmon-dark/60',
  warning: '',
  info: '',
};

/** `Nov 16` style, or an em dash. */
export function shortDate(value: string | null | undefined): string {
  if (!value) return '—';
  return formatDateOnly(value);
}

const MONTH_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });

/** `Nov 2026` for a `YYYY-MM` key or any ISO date in that month — unambiguous next to day dates. */
export function monthLabel(key: string): string {
  return MONTH_YEAR.format(new Date(`${key.slice(0, 7)}-01T00:00:00Z`));
}

export function qty(value: number | null | undefined, uom?: string | null): string {
  if (value == null) return '—';
  const text = fmtQty(value);
  return uom && uom !== 'each' ? `${text} ${uom}` : text;
}

/**
 * Planned order size as the vendor sees it plus what it adds to stock:
 * "4 rolls (7,200 in)". Bought in the stock unit it is just "7,200 in".
 */
export function orderQty(
  order: { quantity: number; purchaseQuantity: number; supplier: PlanningSupplier | null },
  baseUom: string,
  purchase?: { quantity: number; purchaseQuantity: number },
): string {
  const q = purchase ?? order;
  const uom = order.supplier?.purchaseUom;
  if (!uom || (order.supplier?.unitsPerPurchaseUom ?? 1) === 1) return qty(q.quantity, baseUom);
  return `${formatQtyUom(q.purchaseQuantity, uom)} (${qty(q.quantity, baseUom)})`;
}

export function DaysChip({
  days,
  label,
  tone = 'neutral',
}: {
  days: number | null;
  label?: string;
  tone?: 'neutral' | 'warn';
}) {
  if (days == null) return <span className="text-muted-foreground">—</span>;
  return (
    <span
      className={cn(
        'whitespace-nowrap tabular-nums',
        tone === 'warn' ? 'text-fresh-salmon-dark' : 'text-foreground',
      )}
    >
      {days}d{label ? <span className="ml-1 text-xs text-muted-foreground">{label}</span> : null}
    </span>
  );
}

export function SectionTitle({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="text-sm font-medium text-foreground">{children}</h3>
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </div>
  );
}

export function InlineStat({ label, value, tone }: { label: string; value: ReactNode; tone?: 'warn' | 'ok' }) {
  return (
    <div className="rounded-lg border border-border bg-card px-3 py-2">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-0.5 text-sm font-medium tabular-nums',
          tone === 'warn' && 'text-fresh-salmon-dark',
          tone === 'ok' && 'text-bright-moss-dark',
        )}
      >
        {value}
      </div>
    </div>
  );
}
